import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ListPayrollEmployees } from "../../util/API/batch";

const getEmployeeValue = (employee, keys) => {
  const source =
    employee?.User ||
    employee?.Payrollemployee ||
    employee?.Employee ||
    employee?.Customer ||
    employee || {};
  const key = Object.keys(source).find((candidate) =>
    keys.includes(String(candidate).toLowerCase()),
  );

  return key ? source[key] ?? "" : "";
};

export default function PayrollEmployeesReport({ adminId }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");

  const loadEmployees = async () => {
    if (!adminId) {
      setEmployees([]);
      setError("Your payroll administrator profile could not be identified.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await ListPayrollEmployees(adminId);
      if (String(response?.callStatus) !== "1") {
        throw new Error(response?.payload?.msg || "Could not load employees.");
      }

      const payload = response?.payload?.record ?? response?.payload ?? [];
      setEmployees(
        Array.isArray(payload)
          ? payload
          : payload && typeof payload === "object"
            ? [payload]
            : [],
      );
    } catch (requestError) {
      console.error("ListPayrollEmployees failed:", requestError);
      setEmployees([]);
      setError(requestError.message || "Could not load employees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [adminId]);

  const filteredEmployees = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return employees;

    return employees.filter((employee) =>
      [
        getEmployeeValue(employee, ["accountno", "account_number", "accountnumber"]),
        getEmployeeValue(employee, ["firstname", "first_name"]),
        getEmployeeValue(employee, ["lastname", "last_name"]),
        getEmployeeValue(employee, ["birthdate", "dateofbirth", "date_of_birth", "dob"]),
      ].some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [employees, searchText]);

  const downloadEmployeesCsv = () => {
    const headers = ["Account Number", "First Name", "Last Name", "Date of Birth"];
    const rows = filteredEmployees.map((employee) => [
      getEmployeeValue(employee, ["accountno", "account_number", "accountnumber"]),
      getEmployeeValue(employee, ["firstname", "first_name"]),
      getEmployeeValue(employee, ["lastname", "last_name"]),
      getEmployeeValue(employee, ["birthdate", "dateofbirth", "date_of_birth", "dob"]),
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((row) => row.map((value) => JSON.stringify(value ?? "")).join(",")),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "payroll-employees.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#00365A" }}>
            Employees
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Employees linked to your payroll group
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="outlined" onClick={loadEmployees} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" onClick={downloadEmployeesCsv} disabled={loading || filteredEmployees.length === 0}>
            Download CSV
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ color: "#5523D2", fontWeight: 600 }}>
          {loading ? "Loading employees…" : `${filteredEmployees.length} employee${filteredEmployees.length === 1 ? "" : "s"}`}
        </Typography>
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search employees"
          size="small"
          inputProps={{ "aria-label": "Search employees" }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredEmployees.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          {searchText ? "No employees match your search." : "No employees are linked to this payroll administrator."}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Payroll employees">
            <TableHead>
              <TableRow>
                <TableCell>Account Number</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Date of Birth</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((employee, index) => (
                <TableRow key={getEmployeeValue(employee, ["customerid", "id", "userid"]) || index}>
                  <TableCell>{getEmployeeValue(employee, ["accountno", "account_number", "accountnumber"]) || "—"}</TableCell>
                  <TableCell>{getEmployeeValue(employee, ["firstname", "first_name"]) || "—"}</TableCell>
                  <TableCell>{getEmployeeValue(employee, ["lastname", "last_name"]) || "—"}</TableCell>
                  <TableCell>{getEmployeeValue(employee, ["birthdate", "dateofbirth", "date_of_birth", "dob"]) || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
